import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { corporateCMSApi } from "~/Services";
import { PageHeading } from "../MediaCentre/Media.hook";

export interface AirlineServices {
  id: number;
  title: string;
  field_content_english: AirlineServicesData;
  field_content_malay: AirlineServicesData;
  field_content_mandarin: AirlineServicesData;
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}
interface ContentData {
  description: string;
  nextPage: {
    name: string;
    link: string;
  };
}

interface AirlineIncentives {
  id: number;
  field_content_english: {
    data: ContentData;
  };
  field_content_malay: {
    data: ContentData;
  };
  field_content_mandarin: {
    data: ContentData;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AirlineServicesData {
  data: {
    items: Item[];
    nextPage: {
      name: string;
      link: string;
    };
    labelData: {};
  };
}

export interface Item {
  id: string;
  image: string;
  title: string;
  description: string;
  isLarge: boolean;
}

export interface AirportConsultancy {
  id: number;
  title: string;
  field_content_english: AirportConsultancyData;
  field_content_malay: AirportConsultancyData;
  field_content_mandarin: AirportConsultancyData;
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AirportConsultancyData {
  data: {
    items: Item[];
    nextPage: {
      name: string;
      link: string;
    };
    markup: string;
  };
  labelData: {};
}

export interface Item {
  tab: {
    name: string;
    index: string;
  };
  body: string;
}
interface NewsNextPage {
  id: number;
  title: string;
  field_content_english: {
    data: {
      nextPage: {
        name: string;
        link: string;
      };
    };
  };
  field_content_malay: {
    data: {
      nextPage: {
        name: string;
        link: string;
      };
    };
  };
  field_content_mandarin: {
    data: {
      nextPage: {
        name: string;
        link: string;
      };
    };
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}
interface CommercialLeasingOpportunities {
  id: number;
  title: string;
  field_tender_category_ms: string;
  field_tender_category_zh: string;
  field_omd_number_en: string;
  field_omd_number_ms: string;
  field_omd_number_zh: string;
  field_airport_en: string;
  field_airport_ms: string;
  field_airport_zh: string;
  field_leasing_en: string;
  field_leasing_ms: string;
  field_leasing_zh: string;
  field_tenancy_link_en: string;
  field_tenancy_link_ms: string;
  field_tenancy_link_zh: string;
  field_opening_date: string;
  field_closing_date: string;
  field_tender_priority: string;
  field_tender_priority_ms: string;
  field_tender_priority_zh: string;
  field_leasing_order_sequence: number;
  field_pagename_en: string;
  published: string;
  field_tender_created_at: string;
  field_tender_updated_at: string;
}

export interface Form {
  values: {
    natureOfEnquiryValue: NatureOfEnquiryValue[];
    industryValue: IndustryValue[];
  };
  labels: {
    nameText: string;
    natureOfEnquiryText: string;
    companyText: string;
    industryText: string;
    emailText: string;
    subjectText: string;
    messageText: string;
    captcha: {
      titleText: string;
      helpText: string;
      newCaptchaText: string;
    };
    submitButtonText: string;
  };
}

export interface NatureOfEnquiryValue {
  label: string;
  value: string;
}

export interface IndustryValue {
  label: string;
  value: string;
}

export interface Item {
  tab: {
    name: string;
    index: string;
  };
  body: string;
}

interface AirlineServicesAndIncentives {
  id: number;
  title: string;
  field_title_ms: string;
  field_title_zh: string;
  field_airlines_description_en: string;
  field_airline_description_ms: string;
  field_airline_description_zh: string;
  field_airline_image: string;
  field_airline_page_en: string;
  field_airline_page_ms: string;
  field_airline_page_zh: string;
  field_airline_order_sequence: number;
  field_islarge: boolean;
  published: string;
  createdAt: string;
  updatedAt: string;
}

const fetchCommercialDetails = async (id: string): Promise<any> => {
  if (!id) {
    return null;
  }
  const response: any = await corporateCMSApi.get(`/api/get-commercial-details-id/${id}`);
  return response.data[0];
};

const fetchAirportServicesAndIncentivesDetails = async (id: string): Promise<any> => {
  if (!id) {
    return null;
  }
  const response: any = await corporateCMSApi.get(`/api/get-all-airlines-id/${id}`);
  return response.data[0];
};

const useAirlineServices = (
  options?: UseQueryOptions<AirlineServices[], DefaultError, AirlineServices[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<AirlineServices[], DefaultError> => {
  return useQuery<AirlineServices[], DefaultError, AirlineServices[], QueryKey>(
    {
      queryKey: ["airlineServices"],
      queryFn: async (): Promise<AirlineServices[]> => {
        const response = await corporateCMSApi.get<AirlineServices[]>(
          `/api/page-content/all-fields?pageName=business/business-opportunities/airline-services/airline-services-facilities`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useAirlineIncentives = (
  options?: UseQueryOptions<AirlineIncentives[], DefaultError, AirlineIncentives[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<AirlineIncentives[], DefaultError> => {
  return useQuery<AirlineIncentives[], DefaultError, AirlineIncentives[], QueryKey>(
    {
      queryKey: ["airlineIncentives"],
      queryFn: async (): Promise<AirlineIncentives[]> => {
        const response = await corporateCMSApi.get<AirlineIncentives[]>(
          `/api/page-content/all-fields?pageName=business/business-opportunities/airline-services/airline-incentives`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useAirportConsultancy = (
  options?: UseQueryOptions<AirportConsultancy[], DefaultError, AirportConsultancy[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<AirportConsultancy[], DefaultError> => {
  return useQuery<AirportConsultancy[], DefaultError, AirportConsultancy[], QueryKey>(
    {
      queryKey: ["airportConsultancy"],
      queryFn: async (): Promise<AirportConsultancy[]> => {
        const response = await corporateCMSApi.get<AirportConsultancy[]>(
          `/api/page-content/all-fields?pageName=business/business-opportunities/airport-consultancy-overseas-ventures`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useCommercialLeasing = (
  id: string,
  options?: UseQueryOptions<CommercialLeasingOpportunities, DefaultError, CommercialLeasingOpportunities, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<CommercialLeasingOpportunities, DefaultError> => {
  return useQuery<CommercialLeasingOpportunities, DefaultError, CommercialLeasingOpportunities, QueryKey>(
    {
      queryKey: ["commercialLeasingOpportunities", id],
      enabled: false,
      queryFn: () => {
        return fetchCommercialDetails(id);
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useAirportServicesAndIncentives = (
  id: string,
  options?: UseQueryOptions<AirlineServicesAndIncentives, DefaultError, AirlineServicesAndIncentives, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<AirlineServicesAndIncentives, DefaultError> => {
  return useQuery<AirlineServicesAndIncentives, DefaultError, AirlineServicesAndIncentives, QueryKey>(
    {
      queryKey: ["airlineServicesFacilities", id],
      enabled: false,
      queryFn: () => {
        return fetchAirportServicesAndIncentivesDetails(id);
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useAirlineIncentivesPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["airlineIncentivesPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=business/business-opportunities/airline-services/airline-incentives`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useAirlineServicesAndIncentivesPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["airlineServicesFacilitiesPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=business/business-opportunities/airline-services/airline-services-facilities`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useAirportConsultancyAndOverseasVenturesPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["airportConsultancyAndOverseasVenturesPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=business/business-opportunities/airport-consultancy-overseas-ventures`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useCommercialLeasingOpportunitiesPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["commercialLeasingOpportunitiesPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=business/business-opportunities/commercial-leasing-opportunities`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};
const useAirlineServicesFacilitiesNextPage = (
  options?: UseQueryOptions<NewsNextPage[], DefaultError, NewsNextPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<NewsNextPage[], DefaultError> => {
  return useQuery<NewsNextPage[], DefaultError, NewsNextPage[], QueryKey>(
    {
      queryKey: ["airlineServicesFacilitiesNextPage"],
      enabled: false,
      queryFn: async (): Promise<NewsNextPage[]> => {
        const response = await corporateCMSApi.get<NewsNextPage[]>(
          `/api/page-content/all-fields?pageName=business/business-opportunities/airline-services/airline-services-facilities`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export {
  useAirlineIncentives,
  useAirlineIncentivesPageHeading,
  useAirlineServices,
  useAirlineServicesAndIncentivesPageHeading,
  useAirlineServicesFacilitiesNextPage,
  useAirportConsultancy,
  useAirportConsultancyAndOverseasVenturesPageHeading,
  useAirportServicesAndIncentives,
  useCommercialLeasing,
  useCommercialLeasingOpportunitiesPageHeading,
};
